import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { AppDispatch, RootState } from "../../../store/store";
import { resetSupportDebug } from "../../../store/slices/SupportDebugSlice";
import { fetchSupportDebugUserAsync } from "../../../store/thunks/SupportDebugThunk";
import {
  canViewSupportDebug,
  isSupportDebugToolId,
  SUPPORT_DEBUG_TOOLS,
  SupportDebugToolId,
} from "../../../constants/supportDebug";
import { LoadingState } from "./PanelStates";
import UserLookup from "./UserLookup";
import UpcomingBillsPanel from "./panels/UpcomingBillsPanel";
import SafeToSpendPanel from "./panels/SafeToSpendPanel";
import CashflowShortfallPanel from "./panels/CashflowShortfallPanel";

const DEFAULT_TOOL: SupportDebugToolId = "upcoming-bills";

const SupportDebugDashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedUser = useSelector((state: RootState) => state.SupportDebugStore.selectedUser);

  // URL is the source of truth for the active tool and user so views are
  // deep-linkable (?tool=cashflow-shortfall&userId=...).
  const toolParam = searchParams.get("tool");
  const activeTool: SupportDebugToolId = isSupportDebugToolId(toolParam)
    ? toolParam
    : DEFAULT_TOOL;
  const activeToolMeta =
    SUPPORT_DEBUG_TOOLS.find((tool) => tool.id === activeTool) ?? SUPPORT_DEBUG_TOOLS[0];
  const userIdParam = searchParams.get("userId");

  // Load the user detail whenever the userId param points somewhere new (deep
  // links and lookup selections both funnel through the URL). The ref stops a
  // failed load from retrying forever while still refetching on param change.
  const requestedUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!userIdParam) {
      requestedUserIdRef.current = null;
      return;
    }
    if (userIdParam !== requestedUserIdRef.current) {
      requestedUserIdRef.current = userIdParam;
      dispatch(fetchSupportDebugUserAsync(userIdParam));
    }
  }, [userIdParam, dispatch]);

  // Repo convention: reset the slice when leaving the page.
  useEffect(() => {
    return () => {
      dispatch(resetSupportDebug());
    };
  }, [dispatch]);

  const handleSelectUser = useCallback(
    (userId: string) => {
      if (userId === requestedUserIdRef.current) {
        // Same user re-selected (e.g. retry after a failed load): the param
        // won't change, so fetch directly. The pending reducer clears tool data.
        dispatch(fetchSupportDebugUserAsync(userId));
        return;
      }
      setSearchParams((previous) => {
        const params = new URLSearchParams(previous);
        params.set("userId", userId);
        return params;
      });
    },
    [dispatch, setSearchParams]
  );

  const handleChangeUser = useCallback(() => {
    dispatch(resetSupportDebug());
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      params.delete("userId");
      return params;
    });
  }, [dispatch, setSearchParams]);

  const handleToolChange = (toolId: SupportDebugToolId) => {
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      params.set("tool", toolId);
      return params;
    });
  };

  const renderActivePanel = () => {
    if (!selectedUser) {
      return (
        <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
          <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
            <p className="text-sm font-medium text-texthead opacity-70">No user selected</p>
            <p className="max-w-md text-sm text-textColor">
              Look up a user by email or user ID above to run the {activeToolMeta.label} tool.
            </p>
          </div>
        </div>
      );
    }
    switch (activeTool) {
      case "upcoming-bills":
        return <UpcomingBillsPanel userId={selectedUser.id} />;
      case "safe-to-spend":
        return <SafeToSpendPanel userId={selectedUser.id} />;
      case "cashflow-shortfall":
        return <CashflowShortfallPanel userId={selectedUser.id} />;
      default:
        return null;
    }
  };

  return (
    <div className="p-8">
      {/* Page header */}
      <h1 className="desktext10">Product Support</h1>
      <p className="mt-1 font-inter text-sm font-normal leading-5 text-textColor">
        Debug what the Finley app shows a user — look up a user, then run a tool
      </p>

      <UserLookup onSelectUser={handleSelectUser} onChangeUser={handleChangeUser} />

      {/* Tool tabs (rendered from the registry; same styling as GlobalSearch tabs) */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        {SUPPORT_DEBUG_TOOLS.map((tool) => (
          <button
            key={tool.id}
            type="button"
            title={tool.description}
            onClick={() => handleToolChange(tool.id)}
            className={`px-3 py-2 rounded-lg text-sm font-semibold shadow-light-sm transition-colors ${
              activeTool === tool.id
                ? "bg-[#22C55E] text-white border border-[#22C55E]"
                : "bg-white text-textColor3 border border-brColor hover:bg-gray-50"
            }`}
          >
            {tool.label}
          </button>
        ))}
      </div>
      <p className="mt-2 text-sm text-textColor">{activeToolMeta.description}</p>

      {renderActivePanel()}
    </div>
  );
};

const SupportDebug = () => {
  const userAccount = useSelector((state: RootState) => state.OrganizationListStore?.userAccount);

  // Access control: userAccount is fetched in AppLayout; treat its absence as "still loading".
  // Known limitation (shared with Organizations.tsx): OrganizationSlice has no dedicated status for
  // getUserAccountDetailsAsync (its `status` field is shared by many thunks), so a failed account
  // fetch cannot be distinguished here and this spinner persists.
  if (!userAccount) {
    return (
      <div className="p-8">
        <LoadingState />
      </div>
    );
  }

  if (!canViewSupportDebug(userAccount.user_role)) {
    return (
      <div className="p-8">
        <div className="mt-10 flex flex-col items-center justify-center gap-3 text-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-amber-500" />
          <div className="desktext14 text-texthead">Access restricted</div>
          <p className="max-w-md text-sm text-textColor">
            You do not have permission to view product support tools. Please contact an
            administrator if you believe this is a mistake.
          </p>
        </div>
      </div>
    );
  }

  return <SupportDebugDashboard />;
};

export default SupportDebug;
